// #region imports

// --- Angular
import {
  Component,
  OnInit,
  Input,
  ChangeDetectionStrategy,
  Output,
  EventEmitter,
  SimpleChanges,
  OnChanges
} from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';

// --- RxJs
import { Observable, BehaviorSubject } from 'rxjs';

// --- 3rd Party
import moment from 'moment';

// --- HS Code
import {
  ChartClickResponse,
  Constants,
  GroupModel,
  ReportRequestModel,
  RespondentModel,
  RespondentProgressModel,
  ResultsStatuses,
  ResultsViews,
  ScoreModels,
  SurveyModel,
  SurveyResponseTypes,
  SurveySummaryModel,
  SurveyStatuses,
  TrackMyJourneyResultsModel,
  SurveyResponseModel,
} from '../../../models';

// #endregion

@Component({
  selector: 'hs-dashboard-surface',
  templateUrl: './dashboard-surface.component.html',
  styleUrls: ['./dashboard-surface.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('compareResultsModalAnimation', [
      transition(':enter', [
        style({
          opacity: 0,
          transform: 'translateY(100%)',
        }),
        animate('0.5s ease', style({
          opacity: 1,
          transform: 'translateY(0%)',
        }))
      ]),
      transition(':leave', [
        style({
          opacity: 1,
          transform: 'translateY(0%)',
        }),
        animate('0.5s ease', style({
          opacity: 0,
          transform: 'translateY(100%)',
        }))
      ])
    ])
  ]
})
export class DashboardSurfaceComponent implements OnChanges {

  @Input() areResultsLoading: boolean | null = null;
  @Input() compareReportDisplayMode: ResultsViews | null = null;
  @Input() compareReportResults: TrackMyJourneyResultsModel[] | null = null;
  @Input() compareReportResultsLoading: boolean | null = null;
  @Input() errorMessage: string | null = null;
  @Input() imagesRootUrl: string | null = null;
  @Input() isAdding: boolean | null = null;
  @Input() isLoading: boolean | null = null;
  @Input() openPacksFeatureEnabled: boolean = false;
  @Input() reportIsDownLoading: boolean | null = null;
  @Input() reportIsLoading: boolean | null = null;
  @Input() respondentGroups: GroupModel[] | null = null;
  @Input() respondentProgress: RespondentProgressModel | null = null;
  @Input() respondentsExpanded: boolean | null = null;
  @Input() response: SurveyResponseModel | null = null;
  @Input() resultsDisplayMode: ResultsViews | null = null;
  @Input() scoreLists: ScoreModels | null = null;
  @Input() selectedSurvey: SurveySummaryModel | null = null;
  @Input() selfSurveys: SurveySummaryModel[] | null = null;
  @Input() shouldShowSelfRegistration: boolean = false;
  @Input() showCompareResultsModal: boolean | null = null;
  @Input() showRespondentsReminder: boolean | null = null;
  @Input() validationMessageIsbnNumberMaxLength: string | null = null;
  @Input() validationMessageIsbnNumberRequired: string | null = null;
  @Input() validationMessageSurveyNameMaxLength: string | null = null;
  @Input() validationMessageSurveyNameRequired: string | null = null;

  @Output() chartClicked = new EventEmitter<ChartClickResponse>();
  @Output() closeReminder = new EventEmitter();
  @Output() compareResultsWidgetDisplayChanged = new EventEmitter<ResultsViews>();
  @Output() createSurveyClicked = new EventEmitter<SurveySummaryModel>();
  @Output() downloadReportClicked = new EventEmitter<string>();
  @Output() downloadTrackMyJourneyClicked = new EventEmitter<ReportRequestModel>();
  @Output() exploreResults = new EventEmitter<ChartClickResponse>();
  @Output() manageGroups = new EventEmitter();
  @Output() managePartnersClicked = new EventEmitter<SurveySummaryModel>();
  @Output() manageRespondentsButtonClicked = new EventEmitter<string>();
  @Output() openCompareResultsHint = new EventEmitter();
  @Output() openResultsHint = new EventEmitter();
  @Output() resultsWidgetDisplayChanged = new EventEmitter<ResultsViews>();
  @Output() selfRegisterButtonClicked = new EventEmitter();
  @Output() sendReminder = new EventEmitter<RespondentModel>();
  @Output() startContinueButtonClicked = new EventEmitter<SurveySummaryModel>();
  @Output() surveyNameChanged = new EventEmitter<string>();
  @Output() toggleRespondents = new EventEmitter<boolean>();
  @Output() toggleCompareResultsModal = new EventEmitter<boolean>();

  responseTypes = SurveyResponseTypes;
  surveyStatuses = SurveyStatuses;
  resultsStatuses = ResultsStatuses;
  currentDate: Date;
  maximumNumberOfRespondentsPerGroup: number = Constants.NUMBER_OF_RESPONDENTS_PER_GROUP;
  shouldShowRespondentReminders: boolean = false;
  showSelfRegistration: boolean = false;

  constructor() {
    this.isLoading = false;

    this.currentDate = moment()
      .subtract(moment().utcOffset(), 'm')
      .toDate();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes['showRespondentsReminder'] && changes['showRespondentsReminder'].currentValue) {
      if (this.selectedSurvey && this.respondentProgress) {
        this._decideWhetherToShowRespondentReminders();
      }
    }

    if (changes && changes['selectedSurvey'] && changes['selectedSurvey'].currentValue) {
      if (this.showRespondentsReminder && this.respondentProgress) {
        this._decideWhetherToShowRespondentReminders();
      }
    }

    if (changes && changes['respondentProgress'] && changes['respondentProgress'].currentValue) {
      if (this.selectedSurvey && this.showRespondentsReminder) {
        this._decideWhetherToShowRespondentReminders();
      }
    }
  }

  onStartContinueButtonClicked(survey: SurveySummaryModel): void {
    this.startContinueButtonClicked.emit(survey);
  }

  onViewChange(mode: ResultsViews): void {
    this.resultsWidgetDisplayChanged.emit(mode);
  }

  onCompareViewChange(mode: ResultsViews): void {
    this.compareResultsWidgetDisplayChanged.emit(mode);
  }

  onDownloadReportClicked(identifier: string) {
    this.downloadReportClicked.emit(identifier);
  }

  onOpenResultsHint() {
    this.openResultsHint.emit();
  }

  onManageRespondentsButtonClicked(identifier: string) {
    this.manageRespondentsButtonClicked.emit(identifier);
  }

  onToggleRespondents(expand: boolean) {
    this.toggleRespondents.emit(expand);
  }

  onCloseReminder() {
    this.closeReminder.emit();
  }

  onManageGroups() {
    this.manageGroups.emit();
  }

  onSendReminder(respondent: RespondentModel) {
    this.sendReminder.emit(respondent);
  }

  onChartClick(responseType: SurveyResponseTypes, selectedSurvey: SurveySummaryModel | SurveyModel) {
    this.chartClicked.emit({
      responseType,
      selectedSurvey,
    });
  }

  onToggleCompareResultsModal(show: boolean) {
    this.toggleCompareResultsModal.emit(show);
  }

  onDownloadTrackMyJourneyClicked(model: ReportRequestModel) {
    this.downloadTrackMyJourneyClicked.emit(model);
  }

  onExploreResults(chartClickResponse: ChartClickResponse) {
    this.exploreResults.emit(chartClickResponse);
  }

  onOpenCompareResultsHint() {
    this.openCompareResultsHint.emit();
  }

  onRegisterClicked(): void {
    this.selfRegisterButtonClicked.emit();
  }

  private _decideWhetherToShowRespondentReminders(): void {
    if (!this.showRespondentsReminder || !this.selectedSurvey || !this.respondentProgress) {
      this.shouldShowRespondentReminders = false;
      return;
    }

    if (this.selectedSurvey.status < SurveyStatuses.indicatorQuestions || this.selectedSurvey.isLocked) {
      this.shouldShowRespondentReminders = false;
      return;
    }

    if (this.selectedSurvey.numberOfRespondents <= this.respondentProgress.numberOfRespondents) {
      this.shouldShowRespondentReminders = false;
      return;
    }

    this.shouldShowRespondentReminders = true;
  }
}
